import type { UploadFile } from 'antd/es/upload/interface';
import request from './request';

export type WaybillAuditIssue = {
  code: string;
  label: string;
  message: string;
};

export type WaybillAuditResult = {
  ok: boolean;
  issues: WaybillAuditIssue[];
  fileName: string;
  parsed?: any;
};

const normalizeWaybillValue = (v: any) => String(v ?? '').replace(/\s+/g, '').trim().toUpperCase();

export const auditWaybillFile = async (
  file: UploadFile<any>,
  trackingNo: string,
): Promise<WaybillAuditResult> => {
  const origin = (file as any)?.originFileObj as File | undefined;
  const name = String((origin as any)?.name || file?.name || '附件');
  if (!origin) {
    return { ok: true, issues: [], fileName: name };
  }

  const issues: WaybillAuditIssue[] = [];
  const pushIssue = (code: string, label: string, message: string) => {
    issues.push({ code, label, message });
  };

  const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
  const allowed = ['pdf', 'jpg', 'jpeg', 'png'];
  if (!allowed.includes(String(ext || ''))) {
    pushIssue('file.type', '文件格式', '仅支持 PDF/JPG/PNG');
  }
  const sizeMb = Number((origin as any)?.size || 0) / (1024 * 1024);
  if (Number.isFinite(sizeMb) && sizeMb > 20) {
    pushIssue('file.size', '文件大小', '文件大小不可超过 20MB');
  }
  if (!trackingNo) {
    pushIssue('tracking.missing', '物流跟踪号', '请先填写物流跟踪号后再上传运单');
  }
  if (issues.length > 0) {
    return { ok: false, issues, fileName: name };
  }

  let parsed: any = {};
  try {
    const fd = new FormData();
    fd.append('file', origin);
    const resp = await request.post('/api/v1/logistics/waybill-parse/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    parsed = (resp as any)?.data?.parsed || {};
  } catch (e: any) {
    return {
      ok: false,
      issues: [{
        code: 'parse.failed',
        label: '解析失败',
        message: `运单解析失败：${String(e?.message || e || '未知错误')}`,
      }],
      fileName: name,
    };
  }

  const pageCount = Number(parsed?.page_count ?? 0);
  if (pageCount !== 1) {
    pushIssue('page.count', '页数', '运单文件只能包含 1 页');
    return { ok: false, issues, fileName: name, parsed };
  }
  const first = Array.isArray(parsed?.pages) ? parsed.pages[0] : null;
  const ratio = Number(first?.effective_area_ratio ?? 0);
  if (!(ratio >= 0.6)) {
    pushIssue('page.area_ratio', '有效区域占比', '页面有效区域占比不足 0.6，请检查文件是否留白过多');
  }
  const sim = first?.print_simulation || {};
  if (sim?.barcode_readable !== true) {
    pushIssue('barcode.readable', '模拟打印条码识别', '模拟打印到 4×6 英寸后无法识别条码');
    return { ok: false, issues, fileName: name, parsed };
  }
  const nums = Array.isArray(sim?.barcode_numbers) ? sim.barcode_numbers : [];
  const normTracking = normalizeWaybillValue(trackingNo || '');
  const tailLen = Math.min(12, normTracking.length);
  const tailTracking = tailLen > 0 ? normTracking.slice(-tailLen) : '';
  const normNums = nums.map(normalizeWaybillValue).filter(Boolean);
  const tailNums = normNums.map((v) => (tailLen > 0 ? v.slice(-tailLen) : ''));
  const tailSet = new Set(tailNums.filter(Boolean));
  if (!tailTracking || !tailSet.has(tailTracking)) {
    const typed = trackingNo || '-';
    const decodedList = normNums.length > 0 ? `[${normNums.length}个条码 ${normNums.join(' | ')}]` : '[0个条码 无]';
    pushIssue(
      'tracking.match',
      '跟踪号匹配',
      `填写的物流跟踪号:${typed}\n运单文件条码解析结果:${decodedList}\n结论:填写的跟踪号不在条码解析结果集合中`
    );
  }
  if (issues.length > 0) {
    return { ok: false, issues, fileName: name, parsed };
  }
  return { ok: true, issues: [], fileName: name, parsed };
};
